/**
 * Suche über alle Lieferanten — gebraucht im Projekt (Lieferant je Material,
 * „+ Lieferant"), in der Anfrage und in der Materialansicht („Lieferant
 * hinzufügen").
 *
 * Gibt es ihn noch nicht, legt „＋ … als neuen Lieferanten anlegen" ihn an
 * (Benjamin, 25.09.2026: „Lieferanten, die noch nicht in der Liste sind, aus
 * Projekten heraus erstellen können"). Kennt der Server einen ähnlichen Namen
 * („Hornbach" ↔ „HORNBACH Baumarkt AG"), fragt die Suche nach, statt eine
 * Dublette anzulegen.
 */
import { useMemo, useState } from "react";

import { fehlerText } from "@vimaana/ui";

import { useLieferantenUebersicht, useNeuerLieferant } from "../../hooks/useLieferanten";
import "../../styles/lieferanten.css";

type Props = {
  ausser?: string[];
  onWahl: (id: string) => void;
  onAbbruch?: () => void;
  hinweis?: string;
  /** Liste erst beim Tippen zeigen (für ein dauerhaft sichtbares Suchfeld). */
  nurBeiEingabe?: boolean;
  autoFokus?: boolean;
  platzhalter?: string;
};

export default function LieferantSuche({ ausser = [], onWahl, onAbbruch, hinweis, nurBeiEingabe = false, autoFokus = true, platzhalter = "Lieferant suchen …" }: Props) {
  const daten = useLieferantenUebersicht();
  const neu = useNeuerLieferant();
  const [q, setQ] = useState("");
  const [aehnlich, setAehnlich] = useState<{ id: string; name: string; getippt: string } | null>(null);
  const anlegen = (name: string, force = false) => neu.mutate({ name, force }, {
    onSuccess: (l) => {
      if (!force && l.name.trim().toLowerCase() !== name.trim().toLowerCase()) { setAehnlich({ ...l, getippt: name }); return; }
      setAehnlich(null); setQ(""); onWahl(l.id);
    },
  });
  const treffer = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (daten.data?.lieferanten ?? [])
      .filter((l) => !ausser.includes(l.id) && (!s || l.name.toLowerCase().includes(s)))
      .slice(0, s ? 30 : 8);
  }, [daten.data, q, ausser]);
  const zeigen = !nurBeiEingabe || q.trim().length > 0;
  const name = q.trim();
  const gibtEs = (daten.data?.lieferanten ?? []).some((l) => l.name.trim().toLowerCase() === name.toLowerCase());
  return (
    <div className="lf-mwahl">
      <div className="lf-mwahl-zeile">
        <input className="lf-feld" autoFocus={autoFokus} value={q} onChange={(e) => setQ(e.target.value)} placeholder={platzhalter}
          aria-label="Lieferant suchen" autoComplete="off"
          onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); if (q) setQ(""); else onAbbruch?.(); } }} />
        {onAbbruch && <button type="button" className="lf-knopf" onClick={onAbbruch}>Abbrechen</button>}
      </div>
      {hinweis && <p className="lf-hinweis">{hinweis}</p>}
      {zeigen && (
        <div className="lf-mwahl-liste">
          {daten.isPending && <div className="lf-leer">Lade …</div>}
          {treffer.map((l) => (
            <button key={l.id} type="button" className="lf-mwahl-option" onClick={() => { onWahl(l.id); setQ(""); }}>
              <span className="lf-mwahl-name">{l.name}</span>
              {l.adresse && <span className="lf-mwahl-info">{l.adresse}</span>}
            </button>
          ))}
          {!daten.isPending && treffer.length === 0 && !name && <div className="lf-leer">Kein Lieferant gefunden.</div>}
          {name.length >= 2 && !gibtEs && !aehnlich && (
            <button type="button" className="lf-mwahl-option is-neu" disabled={neu.isPending} onClick={() => anlegen(name)}>
              <span className="lf-mwahl-name">{neu.isPending ? "Legt an …" : `＋ „${name}“ als neuen Lieferanten anlegen`}</span>
              <span className="lf-mwahl-info">Adresse, Ansprechpartner und Warengruppe danach auf seiner Seite</span>
            </button>
          )}
        </div>
      )}
      {aehnlich && (
        <div className="lf-form">
          <span className="lf-hinweis">Es gibt schon „{aehnlich.name}“ — ist das derselbe?</span>
          <div className="lf-knoepfe">
            <button type="button" className="lf-knopf" disabled={neu.isPending} onClick={() => anlegen(aehnlich.getippt, true)}>Nein, „{aehnlich.getippt}“ neu anlegen</button>
            <button type="button" className="lf-knopf is-primaer" onClick={() => { const id = aehnlich.id; setAehnlich(null); setQ(""); onWahl(id); }}>Ja, „{aehnlich.name}“ nehmen</button>
          </div>
        </div>
      )}
      {neu.isError && <div className="lf-fehler">{fehlerText(neu.error)}</div>}
    </div>
  );
}
